/**
 * Skeleton Loading Component
 *
 * Display placeholder content while data is loading.
 * Improves perceived performance and prevents layout shift.
 *
 * @example
 * {loading ? <SkeletonCard /> : <ActualCard data={data} />}
 */

import React from 'react';

/**
 * Base skeleton element with shimmer animation
 */
const Skeleton = ({
  className = '',
  width,
  height,
  rounded = 'rounded',
  animate = true,
}) => {
  const style = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`
        bg-white/10
        ${rounded}
        ${animate ? 'animate-pulse' : ''}
        ${className}
      `}
      style={style}
      aria-hidden="true"
    />
  );
};

/**
 * Text line skeleton
 */
export const SkeletonText = ({
  lines = 1,
  className = '',
  lastLineWidth = '75%',
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className="h-4"
        width={i === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
        rounded="rounded"
      />
    ))}
  </div>
);

/**
 * Avatar/circular skeleton
 */
export const SkeletonAvatar = ({ size = 40, className = '' }) => (
  <Skeleton
    width={size}
    height={size}
    rounded="rounded-full"
    className={className}
  />
);

/**
 * Card skeleton matching the glassmorphism design
 */
export const SkeletonCard = ({ className = '' }) => (
  <div
    className={`bg-white/5 rounded-lg p-4 border border-white/10 ${className}`}
    aria-label="Loading..."
    role="status"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center space-x-3">
        <SkeletonAvatar size={32} />
        <div>
          <Skeleton height={16} width={120} className="mb-2" />
          <Skeleton height={12} width={80} />
        </div>
      </div>
      <Skeleton height={24} width={60} rounded="rounded-full" />
    </div>
    <SkeletonText lines={2} className="mb-3" />
    <div className="flex space-x-2">
      <Skeleton height={20} width={60} rounded="rounded-full" />
      <Skeleton height={20} width={80} rounded="rounded-full" />
    </div>
  </div>
);

/**
 * Stats card skeleton (4 columns)
 */
export const SkeletonStats = ({ count = 4, className = '' }) => (
  <div className={`grid grid-cols-1 md:grid-cols-${count} gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="bg-white/5 rounded-lg p-4 border border-white/10"
      >
        <div className="flex items-center justify-between">
          <div>
            <Skeleton height={12} width={80} className="mb-2" />
            <Skeleton height={28} width={60} />
          </div>
          <Skeleton width={32} height={32} rounded="rounded-lg" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Table row skeleton
 */
export const SkeletonTableRow = ({ columns = 4 }) => (
  <tr className="border-b border-white/5">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="py-3 px-4">
        <Skeleton height={16} width={i === 0 ? '80%' : '60%'} />
      </td>
    ))}
  </tr>
);

/**
 * Table skeleton with header and rows
 */
export const SkeletonTable = ({ rows = 5, columns = 4, className = '' }) => (
  <div className={`overflow-hidden rounded-lg border border-white/10 ${className}`}>
    <table className="w-full">
      <thead className="bg-white/5">
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="py-3 px-4 text-left">
              <Skeleton height={12} width={80} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * List item skeleton
 */
export const SkeletonListItem = ({ className = '' }) => (
  <div className={`flex items-center space-x-3 py-3 ${className}`}>
    <SkeletonAvatar size={36} />
    <div className="flex-1">
      <Skeleton height={14} width="70%" className="mb-1" />
      <Skeleton height={12} width="50%" />
    </div>
    <Skeleton height={20} width={60} rounded="rounded" />
  </div>
);

/**
 * List skeleton
 */
export const SkeletonList = ({ items = 5, className = '' }) => (
  <div className={`divide-y divide-white/5 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <SkeletonListItem key={i} />
    ))}
  </div>
);

/**
 * Chart skeleton (placeholder for visualizations)
 */
export const SkeletonChart = ({ height = 300, className = '' }) => (
  <div
    className={`bg-white/5 rounded-lg border border-white/10 flex items-center justify-center ${className}`}
    style={{ height }}
    aria-label="Loading chart..."
    role="status"
  >
    <div className="text-center">
      <div className="flex items-end justify-center space-x-2 mb-2">
        {[40, 70, 55, 85, 45, 90, 60].map((h, i) => (
          <Skeleton
            key={i}
            width={20}
            height={h}
            rounded="rounded-t"
            className="animate-pulse"
            style={{ animationDelay: `${i * 100}ms` }}
          />
        ))}
      </div>
      <Skeleton height={12} width={100} className="mx-auto" />
    </div>
  </div>
);

/**
 * Navigation tabs skeleton
 */
export const SkeletonTabs = ({ count = 4, className = '' }) => (
  <div className={`flex space-x-2 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton
        key={i}
        height={36}
        width={80 + Math.random() * 40}
        rounded="rounded-lg"
      />
    ))}
  </div>
);

/**
 * Form skeleton
 */
export const SkeletonForm = ({ fields = 3, className = '' }) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i}>
        <Skeleton height={12} width={80} className="mb-2" />
        <Skeleton height={40} width="100%" rounded="rounded-lg" />
      </div>
    ))}
    <Skeleton height={44} width={120} rounded="rounded-lg" className="mt-4" />
  </div>
);

/**
 * Grid of cards skeleton
 */
export const SkeletonCardGrid = ({
  cards = 6,
  columns = 3,
  className = '',
}) => (
  <div
    className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-4 ${className}`}
  >
    {Array.from({ length: cards }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

export default Skeleton;
